/**
 * Profile — redesign v3 "Profile" frame. Tinted header band with the bero
 * lockup + an arch-shaped avatar (brand rule: avatars are arch-shaped — the
 * shared `Avatar` in src/ui is a plain circle used elsewhere, so this arch
 * variant is local, same pattern as home.tsx's CarGlyph) + name + society ·
 * flat · role + a "Verified resident" pill; Edit → personal info; Account /
 * Notifications / Community & safety / Preferences / Support sections;
 * Logout; Admin section gated to SUB_ADMIN/MASTER_ADMIN.
 *
 * NOTE (deviation, flagged): the header band is a two-stop gradient in the
 * design (--header-band); expo-linear-gradient isn't installed and adding
 * packages is out of scope, so it renders as a flat tint
 * (colors.headerBandFrom) — the two stops are visually almost identical so
 * this reads the same. Same as home.tsx — @expo/vector-icons isn't
 * installed, so icon moments reuse the local Feather-style `Icon` set from
 * features/account-admin/icons.tsx. Appearance needs a sun/moon glyph that
 * set doesn't have; a tiny local crescent (two overlapping circles) is added
 * below, in the same View-based spirit — no shared files touched.
 */
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { num, str } from '@/components/gate/api';
import { IconCircle } from '@/features/account-admin/bits';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { BrandBar } from '@/components/shell/BrandBar';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Button, Hairline, Pill, Screen, SectionLabel } from '@/ui';

/** Arch-shaped avatar (brand rule — see BRAND-GUIDELINES §1). */
function ArchAvatar({ name, size = 72 }: { name: string; size?: number }) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const h = Math.round(size * (84 / 72));
  return (
    <View style={{
      width: size, height: h, borderRadius: size / 2,
      borderBottomLeftRadius: size * 0.12, borderBottomRightRadius: size * 0.12,
      backgroundColor: colors.primary500, alignItems: 'center', justifyContent: 'center',
    }}>
      <AppText weight="700" style={{ fontSize: size * 0.32, color: colors.textOnDark }}>{initials}</AppText>
    </View>
  );
}

/* ---------------- moon glyph (Appearance — not in the shared icon set) ---------------- */
function MoonGlyph({ size = 20, color, bg }: { size?: number; color: string; bg: string }) {
  return (
    <View style={{ width: size, height: size }}>
      <View style={{
        position: 'absolute', width: size * 0.76, height: size * 0.76, borderRadius: size * 0.38,
        backgroundColor: color, top: size * 0.06, left: size * 0.04,
      }} />
      <View style={{
        position: 'absolute', width: size * 0.62, height: size * 0.62, borderRadius: size * 0.31,
        backgroundColor: bg, top: -size * 0.02, left: size * 0.3,
      }} />
    </View>
  );
}

/* ---------------- car glyph (Vehicles — not in the shared icon set) ---------------- */
function CarGlyph({ size = 20, color }: { size?: number; color: string }) {
  const s = size / 24;
  const sw = Math.max(1.6, 2 * s);
  return (
    <View style={{ width: size, height: size }}>
      <View style={{
        position: 'absolute', left: 2 * s, top: 9 * s, width: 20 * s, height: 7 * s,
        borderRadius: 3 * s, borderWidth: sw, borderColor: color,
      }} />
      <View style={{
        position: 'absolute', left: 5 * s, top: 5 * s, width: 14 * s, height: 5.5 * s,
        borderTopLeftRadius: 4 * s, borderTopRightRadius: 4 * s,
        borderWidth: sw, borderBottomWidth: 0, borderColor: color,
      }} />
      <View style={{ position: 'absolute', left: 5 * s, top: 14.5 * s, width: 4.5 * s, height: 4.5 * s, borderRadius: 2.25 * s, backgroundColor: color }} />
      <View style={{ position: 'absolute', left: 14.5 * s, top: 14.5 * s, width: 4.5 * s, height: 4.5 * s, borderRadius: 2.25 * s, backgroundColor: color }} />
    </View>
  );
}

/* ---------------- shared row ---------------- */
function ProfileRow({ icon, moon, car, label, value, danger, onPress }: {
  icon?: IconName; moon?: boolean; car?: boolean; label: string; value?: string; danger?: boolean; onPress: () => void;
}) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row', alignItems: 'center', gap: space.s3,
        paddingVertical: 14, paddingHorizontal: space.s4,
        backgroundColor: pressed ? colors.canvas1 : 'transparent',
      })}
    >
      {moon || car ? (
        <View style={{
          width: 40, height: 40, borderRadius: radius.full, backgroundColor: colors.canvas1,
          alignItems: 'center', justifyContent: 'center',
        }}>
          {moon ? (
            <MoonGlyph size={20} color={colors.textSecondary} bg={colors.canvas1} />
          ) : (
            <CarGlyph size={20} color={colors.textSecondary} />
          )}
        </View>
      ) : (
        <IconCircle
          name={icon ?? 'info'}
          bg={danger ? colors.danger50 : colors.canvas1}
          fg={danger ? colors.danger500 : colors.textSecondary}
        />
      )}
      <AppText variant="bodyMd" style={{ flex: 1, color: danger ? colors.danger500 : colors.textPrimary }}>
        {label}
      </AppText>
      {value ? <AppText variant="bodyMd" tone="tertiary">{value}</AppText> : null}
      <Icon name="chevron-right" size={18} color={colors.textTertiary} />
    </Pressable>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const rows = React.Children.toArray(children);
  return (
    <View style={{ marginBottom: space.s6 }}>
      <SectionLabel style={{ paddingHorizontal: space.s4 }}>{title}</SectionLabel>
      <View>
        {rows.map((row, i) => (
          <View key={i}>
            {row}
            {i < rows.length - 1 ? <Hairline inset={space.s4} /> : null}
          </View>
        ))}
      </View>
    </View>
  );
}

/** Endonym for the current language, shown on the Preferences → Language row. */
const LANG_ENDONYM: Record<string, string> = { en: 'English', hi: 'हिन्दी', mr: 'मराठी' };

export default function ProfileTab() {
  const { colors, scheme, toggle } = useTheme();
  const { t } = useT();
  const { user, signOut } = useSession();
  const [societyName, setSocietyName] = React.useState<string | null>(null);
  const isAdmin = user?.role === 'SUB_ADMIN' || user?.role === 'MASTER_ADMIN';
  const isCommittee = user?.role === 'COMMITTEE';

  // Position powers (permissions engine): a RESIDENT holding e.g. the Chairman
  // position must reach the approvals queue / share flow even though their
  // persona isn't committee. Best-effort — rows just stay hidden on failure.
  const [canApprove, setCanApprove] = React.useState(false);
  const [canShare, setCanShare] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<{ perms?: Record<string, string[]> }>('/api/positions/me');
        const perms = body?.perms ?? {};
        if (cancelled) return;
        setCanApprove(Object.values(perms).some((bits) => bits.includes('approve')));
        setCanShare(Object.values(perms).some((bits) => bits.includes('grant')));
      } catch {
        // best-effort only.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<unknown>('/api/societies');
        const list = Array.isArray((body as { societies?: unknown[] })?.societies)
          ? (body as { societies: unknown[] }).societies
          : [];
        const mine = list.find((s) => num(s, 'id') === user?.societyId);
        const name = str(mine, 'name');
        if (!cancelled && name) setSocietyName(name);
      } catch {
        // best-effort only.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.societyId]);

  const roleLabel = user?.role === 'MASTER_ADMIN' ? t('role_master_admin')
    : user?.role === 'SUB_ADMIN' ? t('role_committee_admin')
    : user?.residentType === 'TENANT' ? t('role_tenant')
    : user?.residentType === 'FAMILY' ? t('role_family')
    : t('role_owner');
  const metaLine = [societyName, user?.flat?.code, roleLabel].filter(Boolean).join(' · ');

  const logout = async () => {
    await signOut();
    router.replace('/login');
  };

  return (
    <Screen scroll={false} safeBottom={false} style={{ flex: 1 }}>
      <BrandBar title={t('profile_title')} />
      <Screen padded>
      <View style={{ marginHorizontal: -space.s4, position: 'relative', overflow: 'hidden' }}>
        <LinearGradient
          colors={[colors.headerBandTo, colors.headerBandFrom, colors.canvas0]}
          locations={[0, 0.55, 1]}
          style={StyleSheet.absoluteFill}
        />
        <View style={{ paddingHorizontal: space.s4, paddingTop: space.s4, paddingBottom: space.s5 }}>
        <BrandLockup markHeight={22} />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s4, marginTop: space.s4 }}>
          <ArchAvatar name={user?.fullName ?? 'Resident'} size={72} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <AppText variant="headingLg" display>{user?.fullName ?? 'Resident'}</AppText>
            {metaLine ? <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 2 }}>{metaLine}</AppText> : null}
            <View style={{ marginTop: 6 }}>
              <Pill status="resolved" label={t('profile_verified')} />
            </View>
          </View>
        </View>
        </View>
      </View>
      <View style={{ alignItems: 'center', paddingTop: space.s5 }}>
        <Button
          title={t('profile_edit')}
          kind="secondary"
          onPress={() => router.push('/account/personal-info')}
          style={{ width: 200 }}
        />
      </View>

      <View style={{ marginTop: space.s8 }}>
        <Section title={t('sec_account')}>
          <ProfileRow icon="user" label={t('row_personal_info')} onPress={() => router.push('/account/personal-info')} />
          <ProfileRow icon="home" label={t('row_my_flat')} value={user?.flat?.code} onPress={() => router.push('/account/my-flat')} />
          <ProfileRow icon="users" label={t('row_family')} onPress={() => router.push('/family-pass')} />
          <ProfileRow car label={t('row_vehicles')} onPress={() => router.push('/vehicles')} />
        </Section>

        <Section title={t('sec_notifications')}>
          <ProfileRow icon="bell" label={t('row_notif_inbox')} onPress={() => router.push('/account/notifications')} />
          <ProfileRow icon="smartphone" label={t('np_title')} onPress={() => router.push('/account/notification-prefs')} />
        </Section>

        <Section title={t('sec_community_safety')}>
          <ProfileRow icon="users" label={t('row_residents')} onPress={() => router.push('/residents')} />
          <ProfileRow icon="user-plus" label={t('row_visitor_history')} onPress={() => router.push('/visitors')} />
          <ProfileRow icon="alert-triangle" label={t('row_emergency_sos')} danger onPress={() => router.push('/sos')} />
        </Section>

        <Section title={t('sec_preferences')}>
          <ProfileRow moon label={t('row_appearance')} value={scheme === 'dark' ? t('appearance_dark') : t('appearance_light')} onPress={toggle} />
          <ProfileRow icon="globe" label={t('lang_title')} value={LANG_ENDONYM[user?.language ?? 'en'] ?? user?.language} onPress={() => router.push('/account/language')} />
          <ProfileRow icon="lock" label={t('row_privacy')} onPress={() => router.push('/account/privacy')} />
        </Section>

        <Section title={t('sec_support')}>
          <ProfileRow icon="info" label={t('row_help')} onPress={() => router.push('/account/help')} />
          <ProfileRow icon="message-square" label={t('row_contact')} onPress={() => router.push('/account/contact')} />
          <ProfileRow icon="file-text" label={t('row_terms')} onPress={() => router.push('/account/terms')} />
          <ProfileRow icon="info" label={t('row_about')} onPress={() => router.push('/account/about')} />
        </Section>

        {isAdmin || isCommittee || canApprove || canShare ? (
          <Section title={t('sec_management')}>
            {isAdmin ? (
              <ProfileRow icon="shield" label={t('row_society_admin')} onPress={() => router.push('/admin')} />
            ) : isCommittee ? (
              <ProfileRow icon="megaphone" label={t('row_committee_desk')} onPress={() => router.push('/committee')} />
            ) : null}
            {isAdmin || canApprove ? (
              <ProfileRow icon="clock" label={t('row_pending_actions')} onPress={() => router.push('/pending-actions')} />
            ) : null}
            {canShare ? (
              <ProfileRow icon="key" label={t('row_shared_access')} onPress={() => router.push('/shared-access')} />
            ) : null}
          </Section>
        ) : null}

        <Section title={t('sec_account')}>
          <ProfileRow icon="home" label={t('row_manage_flats')} onPress={() => router.push('/account/manage-flats')} />
          <ProfileRow icon="lock" label={t('row_deactivate')} onPress={() => router.push('/account/deactivate')} />
          <ProfileRow icon="alert-triangle" label={t('row_delete_account')} danger onPress={() => router.push('/account/delete-account')} />
        </Section>

        <View style={{ paddingHorizontal: space.s4 }}>
          <Button title={t('logout')} kind="destructive" onPress={() => void logout()} />
        </View>
      </View>
      </Screen>
    </Screen>
  );
}
